import {useEffect, useRef} from "react";
import {useIsLoggedIn} from "UserProvider";
import {prefetchPrivateData} from "nav/prefetchPrivateData";
import {scheduleIdle} from "nav/scheduleIdle";

/**
 * Idle warm-up for the private-destination LIST DATA (ui#284) — the data sibling of
 * {@link usePrefetchPrivateRoutes} (which warms only the route chunks). The moment the hydration-safe
 * {@link useIsLoggedIn} flag flips true (a returning session after mount, or a fresh sign-in) this
 * schedules a SINGLE background prefetch of the contacts/groups/reminders/transfers caches, so the
 * first open of each destination paints instantly instead of a cold-load skeleton.
 *
 * Fires at most once per mount (a ref latch) and only when logged in; deferred to {@link scheduleIdle}
 * so it never competes with the app's own post-login work. The API token getter awaits Firebase auth
 * readiness, so firing on the `useIsLoggedIn` flip (before the session may have finished restoring) is
 * safe — no request goes out tokenless. The generation guard inside {@link prefetchResource} discards
 * any warm that a logout races. SSG-safe: `window`/`navigator` are touched only after mount.
 *
 * Epic #26: this is the logic seam — a component just calls the hook; it owns the "when/once" policy
 * while {@link prefetchPrivateData} owns the "what to warm".
 */
export function usePrefetchPrivateData(): void {
	const loggedIn = useIsLoggedIn();
	const warmed = useRef(false);
	useEffect(() => {
		if (!loggedIn || warmed.current) {
			return;
		}
		warmed.current = true;
		scheduleIdle(prefetchPrivateData);
	}, [loggedIn]);
}
